import {
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  SimpleGrid,
  Table,
  Tag,
  TagCloseButton,
  TagLabel,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { useSearchParams } from "react-router-dom";
import { Fragment, useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, CaretDown, CaretRight, ChartLine, FlowArrow, ListBullets } from "@phosphor-icons/react";
import { ResponsiveLine } from "@nivo/line";
import { ResponsiveSankey } from "@nivo/sankey";
import { useTranslation } from "react-i18next";
import { DimensionDropdown, QuarterDropdown, YearDropdown } from "../FootPrint";
import YoYChip from "../YoYChip";
import { axisBaselineLayer, chartTheme } from "../Consumption/chartTheme";
import { useBreakdownLevel } from "../../../../../hooks/useBreakdownLevel";
import {
  BY_DIMENSION_PAGE_SIZE,
  QUARTERLY_BY_DIMENSION_PAGE_SIZE,
  dimensionFilterParams,
} from "../../../../../utils/breakdownLevel";
import {
  useGetCompanyDimensionReports,
  useGetCompanyTotalEmissionsReports,
  useGetQuarterlyCompanyDimensionReports,
  useGetQuarterlyCompanyTotalEmissions,
  useGetQuarterlyMultiYearCompanyTotalEmissions,
} from "../../../../../hooks/query/reports";
import QuartersSwitch, { quarterPeriodLabels } from "./QuartersSwitch";
import QuarterlyBreakdownTable, { useQuarterlyTableCompare } from "./QuarterlyBreakdownTable";
import JumpToScope from "./JumpToScope";
import ExpandCollapseControl from "./ExpandCollapseControl";
import { scopeThreeCategories } from "../../../CompanyEmissionsDashboard/components/helpers";
import { CATEGORY_KEY_TO_SCOPE_ENUM } from "../../../../OrganizationQuestionnaire/components/Categories/const";
import { toSafariSafeSankey } from "../../../../../utils/sankey";

// KPI accents — mirror the colours used elsewhere for Scope 3.
const TOTAL_ACCENT = "#5B1D99";
const UPSTREAM_ACCENT = "#9A34F3";
const DOWNSTREAM_ACCENT = "#C786F7";

// Cat 1–8 = upstream; Cat 9–15 = downstream per the GHG Protocol.
const UPSTREAM_INDEXES = [1, 2, 3, 4, 5, 6, 7, 8];
const DOWNSTREAM_INDEXES = [9, 10, 11, 12, 13, 14, 15];


const formatTco2 = (value) => {
  if (value == null || !Number.isFinite(Number(value))) return null;
  return Number(value).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const categoryValue = (data, numberKey) =>
  Number(data?.[`category${numberKey}TotalCo2Emissions`]) || 0;

// ESG-3942: map each frontend category (by its CATEGORIES_KEYS translationKey)
// to the backend ScopeCategoryKey name used in the per-dimension
// scopeThreeCategories breakdown, so we can look up a category's real per-item
// emissions. Categories with no enum entry (none, currently) simply won't match.
const SCOPE_ENUM_BY_TRANSLATION_KEY = CATEGORY_KEY_TO_SCOPE_ENUM;

// Index the per-dimension Scope 3 category breakdown returned by the backend (ESG-3942). Produces
// dimensionItemId → { scopeCategoryName → co2 } so the matrix and the per-item category split are backed
// by real, dimension-scoped data.
//
// ESG-3960: keyed by ID, not name. An org can hold two ACTIVE items sharing a name in one dimension, and
// a name-keyed Map silently kept whichever the query returned last.
const buildCategoryByItem = (dimensionReport) => {
  const byItem = new Map();
  (dimensionReport?.content || []).forEach((row) => {
    if (row.unattributed || row.dimensionItemId == null) return;
    const perCat = {};
    (row.scopeThreeCategories || []).forEach((c) => {
      perCat[c.category] = Number(c.co2Emissions) || 0;
    });
    byItem.set(String(row.dimensionItemId), perCat);
  });
  return byItem;
};

// Build the per-(category × dimension item) emissions matrix from REAL
// per-dimension category data (ESG-3942). Each category's byItem lists only the
// items the backend actually reports emissions for in that category — no
// fabrication. Items are ordered by the configured catalogue for stable display.
const buildMatrix = ({ categories, items, categoryByItem }) => {
  if (!categories || categories.length === 0) return [];
  return categories.map((cat) => {
    const scopeName = SCOPE_ENUM_BY_TRANSLATION_KEY[cat.translationKey];
    const rows = (items || [])
      .map((it) => {
        const value = scopeName ? categoryByItem.get(String(it.id))?.[scopeName] || 0 : 0;
        return { id: it.id, name: it.name, value };
      })
      .filter((r) => r.value > 0);
    return { ...cat, byItem: rows };
  });
};

// For the bottom "By dimension" table: each item's real total Scope 3 with a
// real YoY%. Items with no real total are dropped; YoY is shown only when a
// real previous-year total exists (mirrors the Scope 1/2 discipline — no
// fabricated deltas).
const buildItemRows = ({ items, dimensionTotalsById, prevTotalsById }) => {
  if (!items || items.length === 0) return [];
  return items
    .map((it) => {
      const value = dimensionTotalsById.get(String(it.id)) || 0;
      const prev = prevTotalsById.get(String(it.id));
      const pctChange =
        typeof prev === "number" && prev !== 0 && value > 0 ? ((value - prev) / prev) * 100 : null;
      return { id: it.id, name: it.name, value, pctChange };
    })
    .filter((r) => r.value > 0);
};

const ScopeThreeDetail = ({ year, setYear, quarter, setQuarter, dimension, setDimension }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation("translation", {
    keyPrefix: "dataEntryPlanning.modal.categories",
  });
  // null = whole year; the /reports/total-emissions endpoint expects "ALL".
  const reportQuarter = quarter || "ALL";

  const handleBack = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("detailView");
    setSearchParams(params);
  };

  const { data: currentScope3, isFetching: isFetchingCurrent } = useGetCompanyTotalEmissionsReports(
    { year, quarter: reportQuarter },
    { enabled: !!year }
  );
  const { data: previousScope3, isFetching: isFetchingPrevious } = useGetCompanyTotalEmissionsReports(
    { year: year - 1, quarter: reportQuarter },
    { enabled: !!year }
  );
  // Two-, three- and four-years-ago totals power the 5-year trend chart.
  const { data: yMinus2Scope3 } = useGetCompanyTotalEmissionsReports(
    { year: year - 2, quarter: reportQuarter },
    { enabled: !!year }
  );
  const { data: yMinus3Scope3 } = useGetCompanyTotalEmissionsReports(
    { year: year - 3, quarter: reportQuarter },
    { enabled: !!year }
  );
  const { data: yMinus4Scope3 } = useGetCompanyTotalEmissionsReports(
    { year: year - 4, quarter: reportQuarter },
    { enabled: !!year }
  );

  const categoryRows = useMemo(
    () =>
      scopeThreeCategories.map((cat) => ({
        ...cat,
        label: t(cat.translationKey),
        current: categoryValue(currentScope3, cat.numberKey),
        previous: categoryValue(previousScope3, cat.numberKey),
      })),
    [currentScope3, previousScope3, t]
  );

  const total = currentScope3?.scopeThreeTotalCo2Emissions || 0;
  const previousTotal = previousScope3?.scopeThreeTotalCo2Emissions || 0;

  // Quarterly trend mode: one point per quarter across the same five years.
  // Queries only fire while the switch is on; the page's quarter filter is
  // ignored (all four quarters always plotted). Mirrors EmissionsTrends.jsx.
  const [trendQuarters, setTrendQuarters] = useState(false);
  const trendYears = useMemo(() => [year - 4, year - 3, year - 2, year - 1, year], [year]);
  const quarterlyScope3Queries = useGetQuarterlyMultiYearCompanyTotalEmissions(trendYears, {
    enabled: !!year && trendQuarters,
    retry: false,
    cacheTime: 60_000,
  });
  // Payload per "Q1 2024"-style label (query order is year-major).
  const quarterlyPayloads = useMemo(() => {
    const out = {};
    const labels = quarterPeriodLabels(trendYears);
    quarterlyScope3Queries.forEach((q, i) => {
      if (q.data) out[labels[i]] = q.data;
    });
    return out;
  }, [quarterlyScope3Queries, trendYears]);

  // Five-year Scope 3 total trend. Only periods with a real recorded total are
  // plotted — periods without data are omitted rather than backfilled, matching
  // the Scope 1/2 trend discipline (no fabricated history).
  const trend = useMemo(() => {
    if (trendQuarters) {
      return quarterPeriodLabels(trendYears)
        .map((p) => {
          const v = quarterlyPayloads[p]?.scopeThreeTotalCo2Emissions || 0;
          return { x: p, y: v > 0 ? Number(v.toFixed(2)) : null };
        })
        .filter((p) => p.y != null);
    }
    const reals = [
      yMinus4Scope3?.scopeThreeTotalCo2Emissions || 0,
      yMinus3Scope3?.scopeThreeTotalCo2Emissions || 0,
      yMinus2Scope3?.scopeThreeTotalCo2Emissions || 0,
      previousScope3?.scopeThreeTotalCo2Emissions || 0,
      currentScope3?.scopeThreeTotalCo2Emissions || 0,
    ];
    return trendYears
      .map((yr, i) => ({ x: String(yr), y: reals[i] > 0 ? Number(reals[i].toFixed(2)) : null }))
      .filter((p) => p.y != null);
  }, [trendYears, trendQuarters, quarterlyPayloads, currentScope3, previousScope3, yMinus2Scope3, yMinus3Scope3, yMinus4Scope3]);

  // Per-category 5-year trend: one series per populated category derived
  // from the same five payloads. No extra requests — the totals payload
  // already carries every category total alongside the headline. Used by
  // the trend chart's "All categories" toggle so anomalies (a single
  // category spiking) are easy to spot at a glance.
  const categoryTrends = useMemo(() => {
    // Period-major payload list. In quarterly mode only quarters that
    // returned a report are used — every series shares that same filtered
    // x domain, which the stacked y-scale requires.
    let periodPayloads;
    if (trendQuarters) {
      periodPayloads = quarterPeriodLabels(trendYears)
        .filter((p) => quarterlyPayloads[p])
        .map((p) => [p, quarterlyPayloads[p]]);
    } else {
      const yearPayloads = [yMinus4Scope3, yMinus3Scope3, yMinus2Scope3, previousScope3, currentScope3];
      periodPayloads = trendYears.map((yr, i) => [String(yr), yearPayloads[i]]);
    }
    return scopeThreeCategories
      .map((cat) => {
        const data = periodPayloads.map(([p, payload]) => ({
          x: p,
          y: Number(payload?.[`category${cat.numberKey}TotalCo2Emissions`]) || 0,
        }));
        const peak = data.reduce((acc, p) => Math.max(acc, p.y), 0);
        return { id: `${cat.index}. ${t(cat.translationKey)}`, color: cat.color, data, peak, index: cat.index };
      })
      // Drop categories with no data across any of the periods — they
      // would render as flat zero lines and add noise.
      .filter((s) => s.peak > 0)
      // Largest line first so legend ordering matches visual prominence.
      .sort((a, b) => b.peak - a.peak);
  }, [trendYears, trendQuarters, quarterlyPayloads, t, currentScope3, previousScope3, yMinus2Scope3, yMinus3Scope3, yMinus4Scope3]);
  const upstreamRows = categoryRows.filter((r) => UPSTREAM_INDEXES.includes(r.index));
  const downstreamRows = categoryRows.filter((r) => DOWNSTREAM_INDEXES.includes(r.index));
  const upstream = upstreamRows.reduce((acc, r) => acc + r.current, 0);
  const previousUpstream = upstreamRows.reduce((acc, r) => acc + r.previous, 0);
  const downstream = downstreamRows.reduce((acc, r) => acc + r.current, 0);
  const previousDownstream = downstreamRows.reduce((acc, r) => acc + r.previous, 0);
  const populatedCategories = categoryRows.filter((r) => r.current > 0);

  // ESG-3960: the breakdown level, now user-selectable here too. This page previously hard-coded auto
  // resolution and hid the table entirely at the lowest level (isAtLowestLevel), so a user filtered to
  // the deepest level lost the by-dimension view with no way to pick another.
  const {
    organizationStructure,
    autoLevelKey,
    levelKey: nextLevelKey,
    levelDimension: nextLevelDimension,
    levelName: dimensionLevelName,
    levelOptions,
    onSelectLevel,
    hideByDimension,
  } = useBreakdownLevel(dimension);

  const { data: dimensionReport, isFetching: isFetchingDimension } = useGetCompanyDimensionReports(
    nextLevelDimension?.id,
    {
      year,
      quarter: reportQuarter,
      page: 0,
      size: BY_DIMENSION_PAGE_SIZE,
      includeUnattributed: true,
      ...dimensionFilterParams(dimension),
    },
    { enabled: !!nextLevelDimension?.id && !!year }
  );
  const { data: prevDimensionReport } = useGetCompanyDimensionReports(
    nextLevelDimension?.id,
    { year: year - 1, quarter: reportQuarter, page: 0, size: BY_DIMENSION_PAGE_SIZE, ...dimensionFilterParams(dimension) },
    { enabled: !!nextLevelDimension?.id && !!year }
  );

  // ESG-3960: the item list comes from the report rows themselves, which carry both the id and the name.
  // It used to be seeded from the org-structure catalogue and joined to emissions BY NAME, which needed
  // dedupeByName to stop same-name twins rendering as identical duplicate rows — a workaround that also
  // discarded one twin's real emissions. Rows without data are dropped downstream exactly as before, so
  // the visible set is unchanged for orgs with no duplicate names.
  const fullItemList = useMemo(
    () =>
      (dimensionReport?.content || [])
        .filter((row) => !row.unattributed && row.dimensionItemId != null)
        .map((row) => ({ id: row.dimensionItemId, name: row.dimensionItem })),
    [dimensionReport]
  );
  const dimensionTotalsById = useMemo(() => {
    const m = new Map();
    (dimensionReport?.content || []).forEach((row) => {
      if (row.dimensionItemId != null) m.set(String(row.dimensionItemId), Number(row.scopeThreeTotalCo2Emissions) || 0);
    });
    return m;
  }, [dimensionReport]);
  const prevTotalsById = useMemo(() => {
    const m = new Map();
    (prevDimensionReport?.content || []).forEach((row) => {
      if (row.dimensionItemId != null) m.set(String(row.dimensionItemId), Number(row.scopeThreeTotalCo2Emissions) || 0);
    });
    return m;
  }, [prevDimensionReport]);

  // The "no {level} assigned" Scope 3 figure — on staging this is 80% of Scope 3, hidden until now.
  const unattributedScopeThree = useMemo(() => {
    const footer = (dimensionReport?.content || []).find((row) => row.unattributed);
    const value = Number(footer?.scopeThreeTotalCo2Emissions) || 0;
    return value > 0 ? value : null;
  }, [dimensionReport]);

  const dimensionTotalCount = dimensionReport?.totalElements ?? null;

  // Real per-dimension Scope 3 category breakdown (ESG-3942), indexed by item.
  const categoryByItem = useMemo(() => buildCategoryByItem(dimensionReport), [dimensionReport]);

  // Per-(category × item) matrix and the per-item rollup that powers the
  // bottom table — both from real backend data.
  const matrix = useMemo(
    () => buildMatrix({ categories: populatedCategories, items: fullItemList, categoryByItem }),
    [populatedCategories, fullItemList, categoryByItem]
  );
  const itemRows = useMemo(() => {
    const rows = buildItemRows({ items: fullItemList, dimensionTotalsById, prevTotalsById });
    return rows.sort((a, b) => b.value - a.value || a.name.localeCompare(b.name));
  }, [fullItemList, dimensionTotalsById, prevTotalsById]);
  const itemTotal = itemRows.reduce((acc, r) => acc + r.value, 0);

  // ---- Quarterly by-dimension view ------------------------------------
  // Toggle lives on the page so its queries only fire when the view is on.
  // Period index 0..3 = Q1..Q4, 4 = whole-year ("ALL"). Scope 3 has no kWh —
  // emissions only. Data comes from per-quarter dimension reports; each item
  // carries a 5-slot period array of total Scope 3, plus a per-category
  // roll-down (each category with its own 5-slot period array). Values are
  // real (dimension-scoped ESG-3942 breakdown), not apportioned.
  const [dimQuarterly, setDimQuarterly] = useState(false);
  const quarterlyDimReports = useGetQuarterlyCompanyDimensionReports(
    nextLevelDimension?.id,
    { year, page: 0, size: QUARTERLY_BY_DIMENSION_PAGE_SIZE, ...dimensionFilterParams(dimension) },
    { enabled: !!nextLevelDimension?.id && !!year && dimQuarterly, retry: false, cacheTime: 60_000 }
  );
  const isFetchingQuarterlyDim = dimQuarterly && quarterlyDimReports.some((q) => q.isFetching);

  // Per-item quarterly rows for the "By dimension" table: total Scope 3 per
  // period + a per-category roll-down.
  const quarterlyItemRows = useMemo(() => {
    if (!dimQuarterly) return [];
    // ESG-3960: id-keyed. Keyed by name, two same-name items were folded into one row summing both.
    const byId = new Map();
    const ensure = (id, name) => {
      const key = String(id);
      if (!byId.has(key)) {
        byId.set(key, { id, name, periods: Array(5).fill(0), catMap: new Map() });
      }
      return byId.get(key);
    };
    quarterlyDimReports.forEach((q, pi) => {
      (q.data?.content || []).forEach((row) => {
        if (row.unattributed || row.dimensionItemId == null) return;
        const rec = ensure(row.dimensionItemId, row.dimensionItem);
        rec.periods[pi] = Number(row.scopeThreeTotalCo2Emissions) || 0;
        (row.scopeThreeCategories || []).forEach((c) => {
          if (!rec.catMap.has(c.category)) rec.catMap.set(c.category, { category: c.category, periods: Array(5).fill(0) });
          rec.catMap.get(c.category).periods[pi] += Number(c.co2Emissions) || 0;
        });
      });
    });
    return [...byId.values()]
      .map((rec) => ({
        id: rec.id,
        name: rec.name,
        periods: rec.periods,
        categories: [...rec.catMap.values()]
          .filter((c) => c.periods.some((v) => v > 0))
          .map((c) => ({
            ...c,
            // Resolve the display label from the category enum via the config.
            label: (() => {
              const cfg = scopeThreeCategories.find((sc) => SCOPE_ENUM_BY_TRANSLATION_KEY[sc.translationKey] === c.category);
              return cfg ? `${cfg.index}. ${t(cfg.translationKey)}` : c.category;
            })(),
            index: (() => {
              const cfg = scopeThreeCategories.find((sc) => SCOPE_ENUM_BY_TRANSLATION_KEY[sc.translationKey] === c.category);
              return cfg ? cfg.index : 99;
            })(),
          }))
          .sort((a, b) => a.index - b.index),
      }))
      .filter((r) => r.periods.some((v) => v > 0))
      .sort((a, b) => (b.periods[4] || 0) - (a.periods[4] || 0) || a.name.localeCompare(b.name));
  }, [dimQuarterly, quarterlyDimReports, t]);

  // Per-category quarterly rows for the "By category" table: total per period
  // per category + a per-item roll-down.
  const quarterlyCategoryRows = useMemo(() => {
    if (!dimQuarterly) return [];
    // catEnum → { periods[5], itemMap: name → periods[5] }
    const byCat = new Map();
    quarterlyDimReports.forEach((q, pi) => {
      (q.data?.content || []).forEach((row) => {
        if (row.unattributed || row.dimensionItemId == null) return;
        const itemKey = String(row.dimensionItemId);
        (row.scopeThreeCategories || []).forEach((c) => {
          if (!byCat.has(c.category)) byCat.set(c.category, { category: c.category, periods: Array(5).fill(0), itemMap: new Map() });
          const rec = byCat.get(c.category);
          const v = Number(c.co2Emissions) || 0;
          rec.periods[pi] += v;
          if (!rec.itemMap.has(itemKey)) {
            rec.itemMap.set(itemKey, { id: row.dimensionItemId, name: row.dimensionItem, periods: Array(5).fill(0) });
          }
          rec.itemMap.get(itemKey).periods[pi] += v;
        });
      });
    });
    return scopeThreeCategories
      .map((cfg) => {
        const rec = byCat.get(SCOPE_ENUM_BY_TRANSLATION_KEY[cfg.translationKey]);
        if (!rec) return null;
        return {
          label: `${cfg.index}. ${t(cfg.translationKey)}`,
          index: cfg.index,
          periods: rec.periods,
          items: [...rec.itemMap.values()]
            .filter((it) => it.periods.some((v) => v > 0))
            .sort((a, b) => (b.periods[4] || 0) - (a.periods[4] || 0) || a.name.localeCompare(b.name)),
        };
      })
      .filter((c) => c && c.periods.some((v) => v > 0));
  }, [dimQuarterly, quarterlyDimReports, t]);

  const loading = isFetchingCurrent || isFetchingPrevious;

  return (
    <Flex direction="column" gap={6}>
      <Flex justifyContent="space-between" alignItems="center" gap={3} flexWrap="wrap">
        <Button leftIcon={<ArrowLeft size={18} />} variant="plain" colorScheme="gray" onClick={handleBack}>
          Footprint
        </Button>
        <HStack spacing={2}>
        <JumpToScope current="scopeThree" />
        <Menu>
          <MenuButton
            as={Button}
            size="sm"
            borderRadius="full"
            border="1px solid"
            borderColor="gray.300"
            bg="transparent"
            color="gray.600"
            fontWeight="normal"
            rightIcon={<CaretDown size={14} />}
            _hover={{ bg: "gray.50", borderColor: "gray.400" }}
          >
            Jump to category
          </MenuButton>
          <MenuList
            borderRadius="lg"
            border="1px solid"
            borderColor="gray.200"
            boxShadow="md"
            py={1}
            maxH="320px"
            overflowY="auto"
            zIndex={2}
          >
            {scopeThreeCategories.map((c) => (
              <MenuItem
                key={c.numberKey}
                fontSize="sm"
                color="gray.600"
                py={1.5}
                onClick={() => {
                  const params = new URLSearchParams(searchParams);
                  params.set("detailView", "scopeThreeCategory");
                  params.set("category", c.numberKey);
                  setSearchParams(params);
                }}
              >
                {`${c.index}. ${t(c.translationKey)}`}
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
        </HStack>
      </Flex>

      <Flex justify="space-between" align="flex-start" gap={4} flexWrap="wrap">
        <Box>
          <Text fontSize="11px" fontWeight="700" letterSpacing="0.06em" color="limeShade.600" textTransform="uppercase" mb={1}>
            Value chain
          </Text>
          <Text fontSize="40px" fontWeight="700" color="darkTeal" lineHeight="1.1">
            Scope 3 - Indirect Emissions
          </Text>
        </Box>
        <Flex gap={2} mt={2}>
          {setDimension && <DimensionDropdown selected={dimension || {}} setSelected={setDimension} />}
          {setYear && <YearDropdown setYear={setYear} selectedYear={year} />}
          {setQuarter && <QuarterDropdown setQuarter={setQuarter} selectedQuarter={quarter} />}
        </Flex>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6} mt={6}>
        <KpiCard accent="pureBlack" title="Total" subtitle="All Scope 3 categories" tco2e={total} previousTco2e={previousTotal} loading={loading} />
        <KpiCard accent={UPSTREAM_ACCENT} title="Upstream" subtitle="Cat 1–8" tco2e={upstream} previousTco2e={previousUpstream} loading={loading} />
        <KpiCard accent={DOWNSTREAM_ACCENT} title="Downstream" subtitle="Cat 9–15" tco2e={downstream} previousTco2e={previousDownstream} loading={loading} />
      </SimpleGrid>

      <Scope3Visualisation
        trend={trend}
        categoryTrends={categoryTrends}
        accent={TOTAL_ACCENT}
        upstream={upstream}
        downstream={downstream}
        categoryRows={categoryRows}
        trendQuarters={trendQuarters}
        setTrendQuarters={setTrendQuarters}
        year={year}
        t={t}
      />

      <CategoryTable
        matrix={matrix}
        total={total}
        isAtLowestLevel={hideByDimension}
        nextLevelName={dimensionLevelName}
        onOpenCategory={(numberKey) => {
          const params = new URLSearchParams(searchParams);
          params.set("detailView", "scopeThreeCategory");
          params.set("category", numberKey);
          setSearchParams(params);
        }}
        quarterly={dimQuarterly}
        setQuarterly={setDimQuarterly}
        quarterlyRows={quarterlyCategoryRows}
        quarterlyLoading={isFetchingQuarterlyDim}
        year={year}
      />

      {/* ESG-3960: shown unless the org has no dimensions at all. It used to be hidden whenever auto
          resolution had no next level (i.e. the filter sat at the lowest level), which removed the table
          along with the only control that could have chosen another level. */}
      {!hideByDimension && (
        <DimensionTable
          itemRows={itemRows}
          itemTotal={itemTotal}
          matrix={matrix}
          levelName={dimensionLevelName}
          unattributed={unattributedScopeThree}
          shownCount={itemRows.length}
          totalCount={dimensionTotalCount}
          levelOptions={levelOptions}
          selectedLevelKey={nextLevelKey}
          autoLevelKey={autoLevelKey}
          onSelectLevel={onSelectLevel}
          loading={isFetchingDimension}
          quarterly={dimQuarterly}
          setQuarterly={setDimQuarterly}
          quarterlyRows={quarterlyItemRows}
          quarterlyLoading={isFetchingQuarterlyDim}
          year={year}
        />
      )}
    </Flex>
  );
};

// Same layout as Scope 1's KpiCard: an inline-block wrapper hugs the
// number+chip cluster intrinsically, so the number renders at the full
// clamp size and the chip+unit always sit flush against its right edge.
// Outer card has overflow:hidden so on extreme narrow viewports the
// cluster clips at the card boundary instead of pushing past it.
const KpiCard = ({ accent, title, subtitle, tco2e, previousTco2e, loading }) => {
  const hasCurrent = typeof tco2e === "number";
  const hasPrev = typeof previousTco2e === "number" && previousTco2e !== 0;
  const pctChange = hasCurrent && hasPrev ? ((tco2e - previousTco2e) / previousTco2e) * 100 : null;

  return (
    <Box position="relative" pt={3} minW={0} overflow="hidden">
      <Box position="absolute" top={0} left={0} right={0} h="3px" bg={accent} borderRadius="full" />
      <HStack align="baseline" spacing={2} mb={2}>
        <Text fontSize="15px" fontWeight="700" color="darkTeal">{title}</Text>
        <Text fontSize="13px" color="gray.500" noOfLines={1}>{subtitle}</Text>
      </HStack>
      <Box
        position="relative"
        display="inline-block"
        whiteSpace="nowrap"
        sx={{ "--big": "clamp(24px, 2.6vw, 56px)" }}
      >
        <Flex align="flex-end" gap={3}>
          <Text
            fontSize="var(--big)"
            fontWeight="700"
            color="darkTeal"
            lineHeight="1"
            letterSpacing="-0.02em"
          >
            {loading ? "…" : (hasCurrent ? formatTco2(tco2e) : "—")}
          </Text>
          <Flex direction="column" justify="space-between" alignSelf="stretch" flexShrink={0}>
            {pctChange != null ? <YoYChip pctChange={pctChange} /> : <Box />}
            <Text fontSize="calc(var(--big) * 0.36)" fontWeight="600" color="gray.600" lineHeight="1">
              tCO₂e
            </Text>
          </Flex>
        </Flex>
      </Box>
    </Box>
  );
};

// "By category" — header band, accent square, chevron column. Each row
// shows a category with share-of-Scope-3 bar, emissions + inline YoY%, and
// an expansion that lists every next-level dimension item with its scaled
// emissions and share-of-category. Mirrors the Scope 1 by-dimension table.
const CategoryTable = ({ matrix, total, isAtLowestLevel, nextLevelName, onOpenCategory, quarterly = false, setQuarterly = null, quarterlyRows = [], quarterlyLoading = false, year = null }) => {
  const [expanded, setExpanded] = useState({});
  const toggle = (k) => setExpanded((prev) => ({ ...prev, [k]: !prev[k] }));
  const rowKeys = matrix.map((c) => c.index);
  const openCount = rowKeys.filter((k) => expanded[k]).length;
  const expandAll = () => setExpanded(Object.fromEntries(rowKeys.map((k) => [k, true])));
  const collapseAll = () => setExpanded({});

  if (matrix.length === 0 && !quarterly) {
    return (
      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={8}>
        <Text textAlign="center" color="gray.500">
          No Scope 3 emissions data has been entered for this year.
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Flex justify="space-between" align="flex-end" mb={3}>
        <Text fontSize="22px" fontWeight="700" color="darkTeal">By category</Text>
        <HStack spacing={4}>
          {setQuarterly && <QuartersSwitch isChecked={quarterly} onChange={setQuarterly} />}
          <Text fontSize="13px" color="gray.500">
            {quarterly
              ? "Category emissions by quarter (tCO₂e); expand for the dimension split."
              : isAtLowestLevel
                ? "Filter is at the lowest dimension level — no further breakdown."
                : `Click a row to break down by ${nextLevelName || "the next dimension"}.`}
          </Text>
        </HStack>
      </Flex>
      {quarterly ? (
        <ScopeThreeQuarterlyTable
          firstColLabel="SCOPE 3 CATEGORIES"
          childLabel={nextLevelName || "Dimension"}
          rows={quarterlyRows}
          loading={quarterlyLoading}
          year={year}
        />
      ) : (
      <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden" bg="white">
        <Table size="sm" variant="unstyled">
          <Thead>
            <Tr bg="gray.50">
              <Th py={3} px={4} colSpan={2} textAlign="left" fontSize="11px" color={TOTAL_ACCENT} textTransform="uppercase" letterSpacing="0.04em" fontWeight="700">
                <HStack spacing={2}>
                  <Box w="8px" h="8px" bg={TOTAL_ACCENT} borderRadius="sm" />
                  <Text>Scope 3 categories</Text>
                  {!isAtLowestLevel && rowKeys.length > 0 && (
                    <ExpandCollapseControl
                      allExpanded={openCount === rowKeys.length}
                      onExpandAll={expandAll}
                      onCollapseAll={collapseAll}
                    />
                  )}
                </HStack>
              </Th>
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">Emissions (tCO₂e)</Th>
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">Share of Scope 3</Th>
              <Th py={3} px={4} width="48px" />
            </Tr>
          </Thead>
          <Tbody>
            {matrix.map((cat, idx) => {
              const isOpen = !!expanded[cat.index];
              const share = total > 0 ? cat.current / total : 0;
              const accent = cat.color || TOTAL_ACCENT;
              const hasPrev = typeof cat.previous === "number" && cat.previous !== 0;
              const yoy = hasPrev ? ((cat.current - cat.previous) / cat.previous) * 100 : null;
              const rowClickable = !isAtLowestLevel && cat.byItem.length > 0;

              return (
                <Fragment key={cat.index}>
                  <Tr
                    borderTop={idx === 0 ? "none" : "1px solid"}
                    borderColor="gray.100"
                    cursor={rowClickable ? "pointer" : "default"}
                    _hover={rowClickable ? { bg: "gray.50" } : undefined}
                    onClick={rowClickable ? () => toggle(cat.index) : undefined}
                  >
                    <Td py={4} pl={4} pr={1} width="32px">
                      {rowClickable ? (
                        <IconButton
                          size="xs"
                          variant="ghost"
                          aria-label={isOpen ? "Collapse" : "Expand"}
                          icon={isOpen ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
                          onClick={(e) => { e.stopPropagation(); toggle(cat.index); }}
                        />
                      ) : (
                        <Box w="20px" />
                      )}
                    </Td>
                    <Td py={4} pl={1} pr={4} fontSize="14px" color="gray.700" fontWeight="500" textAlign="left">
                      {cat.index}. {cat.label}
                    </Td>
                    <Td py={4} px={4} fontSize="14px" color="gray.700" fontWeight="600" textAlign="right">
                      {formatTco2(cat.current)}{" "}
                      {yoy != null && (
                        <Text as="span" fontSize="11px" color={yoy < 0 ? "green.600" : "red.600"} fontWeight="600" ml={2}>
                          {yoy < 0 ? "↓" : "↑"} {Math.abs(yoy).toFixed(1)}%
                        </Text>
                      )}
                    </Td>
                    <Td py={4} px={4} textAlign="left" minW="220px">
                      <HStack spacing={3}>
                        <Box flex="1" h="6px" bg="gray.100" borderRadius="full" overflow="hidden">
                          <Box h="100%" w={`${share * 100}%`} bg={accent} borderRadius="full" />
                        </Box>
                        <Text fontSize="13px" color="gray.700" fontWeight="600" minW="48px" textAlign="right">
                          {(share * 100).toFixed(1)}%
                        </Text>
                      </HStack>
                    </Td>
                    <Td py={4} px={4} width="48px" textAlign="right">
                      <IconButton
                        size="xs"
                        variant="ghost"
                        aria-label={`Open ${cat.label} detail`}
                        icon={<ArrowRight size={14} weight="bold" />}
                        onClick={(e) => { e.stopPropagation(); onOpenCategory?.(cat.numberKey); }}
                      />
                    </Td>
                  </Tr>

                  {isOpen && rowClickable && (
                    <>
                      <Tr bg="gray.50">
                        <Td py={2} pl={4} pr={1} width="32px" />
                        <Td py={2} pl={1} pr={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">
                          {nextLevelName || "Dimension"}
                        </Td>
                        <Td py={2} px={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">Emissions (tCO₂e)</Td>
                        <Td py={2} px={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">Share of category</Td>
                        <Td py={2} px={4} width="48px" />
                      </Tr>
                      {cat.byItem
                        .slice()
                        .sort((a, b) => b.value - a.value || a.name.localeCompare(b.name))
                        .map((d) => {
                          const itemShare = cat.current > 0 ? d.value / cat.current : 0;
                          return (
                            <Tr key={`${cat.index}-${d.name}`} bg="gray.50">
                              <Td py={2} pl={4} pr={1} width="32px" />
                              <Td py={2} pl={1} pr={4} fontSize="13px" color="gray.700">
                                <HStack spacing={2}>
                                  <Box w="8px" h="8px" bg={accent} borderRadius="sm" />
                                  <Text>{d.name}</Text>
                                </HStack>
                              </Td>
                              <Td py={2} px={4} fontSize="13px" color="gray.700" fontWeight="600" textAlign="right">
                                {formatTco2(d.value)}
                              </Td>
                              <Td py={2} px={4} textAlign="left" minW="220px">
                                <HStack spacing={3}>
                                  <Box flex="1" h="5px" bg="gray.100" borderRadius="full" overflow="hidden">
                                    <Box h="100%" w={`${itemShare * 100}%`} bg={accent} borderRadius="full" />
                                  </Box>
                                  <Text fontSize="12px" color="gray.700" fontWeight="600" minW="44px" textAlign="right">
                                    {(itemShare * 100).toFixed(1)}%
                                  </Text>
                                </HStack>
                              </Td>
                              <Td py={2} px={4} width="48px" />
                            </Tr>
                          );
                        })}
                    </>
                  )}
                </Fragment>
              );
            })}
            {matrix.length > 0 && (() => {
              const prevTotal = matrix.reduce((acc, c) => acc + (typeof c.previous === "number" ? c.previous : 0), 0);
              const totalYoy = prevTotal !== 0 ? ((total - prevTotal) / prevTotal) * 100 : null;
              return (
                <Tr bg="gray.50" borderTop="2px solid" borderColor="gray.200">
                  <Td py={4} pl={4} pr={1} width="32px" />
                  <Td py={4} pl={1} pr={4} fontSize="13px" color="darkTeal" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em" textAlign="left">Total</Td>
                  <Td py={4} px={4} fontSize="14px" color="darkTeal" fontWeight="700" textAlign="right">
                    {formatTco2(total)}{" "}
                    {totalYoy != null && (
                      <Text as="span" fontSize="11px" color={totalYoy < 0 ? "green.600" : "red.600"} fontWeight="600" ml={2}>
                        {totalYoy < 0 ? "↓" : "↑"} {Math.abs(totalYoy).toFixed(1)}%
                      </Text>
                    )}
                  </Td>
                  <Td py={4} px={4} textAlign="left" minW="220px">
                    <HStack spacing={3}>
                      <Box flex="1" h="6px" bg="gray.100" borderRadius="full" overflow="hidden">
                        <Box h="100%" w="100%" bg="darkTeal" borderRadius="full" />
                      </Box>
                      <Text fontSize="13px" color="darkTeal" fontWeight="700" minW="48px" textAlign="right">100.0%</Text>
                    </HStack>
                  </Td>
                  <Td py={4} px={4} width="48px" />
                </Tr>
              );
            })()}
          </Tbody>
        </Table>
      </Box>
      )}
    </Box>
  );
};

// Shared quarter-by-quarter table for the Scope 3 by-category and
// by-dimension views. Parent rows carry a 5-slot period array [Q1..Q4, Year]
// of emissions (tCO₂e, no kWh — Scope 3 has no consumption); each expands to
// child rows (dimension items under a category, or categories under a
// dimension item) with the same period columns. The Year column is the
// whole-year figure and may exceed ΣQ1..Q4 when data was entered yearly.
const S3_PERIOD_HEADS = ["Q1", "Q2", "Q3", "Q4", "Year"];
const fmtS3QuarterCell = (v) => (v > 0 ? formatTco2(v) : "—");
const ScopeThreeQuarterlyTable = ({ firstColLabel, childLabel, rows = [], loading = false, year = null }) => {
  const [expanded, setExpanded] = useState({});
  const toggle = (key) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  // Row identity: by-category rows have `label` (unique), by-dimension have `name`.
  const rowKey = (r) => r.name || r.label;
  const childRows = (r) => r.items || r.categories || [];
  const expandableKeys = rows.filter((r) => childRows(r).length > 0).map(rowKey);
  const openCount = expandableKeys.filter((k) => expanded[k]).length;
  const expandAll = () => setExpanded(Object.fromEntries(expandableKeys.map((k) => [k, true])));
  const collapseAll = () => setExpanded({});

  const colTotals = S3_PERIOD_HEADS.map((_, pi) => rows.reduce((acc, r) => acc + (r.periods[pi] || 0), 0));

  return (
    <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden" bg="white">
      <Table size="sm" variant="unstyled">
        <Thead>
          <Tr bg="gray.50">
            <Th py={3} px={4} colSpan={2} textAlign="left" fontSize="11px" color={TOTAL_ACCENT} textTransform="uppercase" letterSpacing="0.04em" fontWeight="700">
              <HStack spacing={2}>
                <Box w="8px" h="8px" bg={TOTAL_ACCENT} borderRadius="sm" />
                <Text>{firstColLabel}</Text>
                {expandableKeys.length > 0 && (
                  <ExpandCollapseControl
                    allExpanded={openCount === expandableKeys.length}
                    onExpandAll={expandAll}
                    onCollapseAll={collapseAll}
                  />
                )}
              </HStack>
            </Th>
            {S3_PERIOD_HEADS.map((h) => (
              <Th key={h} py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">
                {h === "Year" ? `${year || ""} Total` : h}
                <Text as="span" fontSize="9px" color="gray.400" fontWeight="500" textTransform="none"> tCO₂e</Text>
              </Th>
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {loading ? (
            <Tr><Td colSpan={7} py={6} textAlign="center" color="gray.400" fontSize="13px">Loading…</Td></Tr>
          ) : rows.length === 0 ? (
            <Tr><Td colSpan={7} py={6} textAlign="center" color="gray.400" fontSize="13px">No quarterly Scope 3 data for this year.</Td></Tr>
          ) : (
            rows.map((row, idx) => {
              const key = rowKey(row);
              const children = childRows(row);
              const hasChildren = children.length > 0;
              const isOpen = hasChildren && !!expanded[key];
              return (
                <Fragment key={key}>
                  <Tr
                    borderTop={idx === 0 ? "none" : "1px solid"}
                    borderColor="gray.100"
                    cursor={hasChildren ? "pointer" : "default"}
                    _hover={hasChildren ? { bg: "gray.50" } : undefined}
                    onClick={hasChildren ? () => toggle(key) : undefined}
                  >
                    <Td py={4} pl={4} pr={1} width="32px">
                      {hasChildren && (
                        <IconButton
                          size="xs"
                          variant="ghost"
                          aria-label={isOpen ? "Collapse" : "Expand"}
                          icon={isOpen ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
                          onClick={(e) => { e.stopPropagation(); toggle(key); }}
                        />
                      )}
                    </Td>
                    <Td py={4} pl={1} pr={4} fontSize="14px" color="gray.700" fontWeight="500" textAlign="left">{row.label || row.name}</Td>
                    {row.periods.map((v, pi) => (
                      <Td key={pi} py={4} px={4} fontSize="13px" fontWeight={pi === 4 ? "700" : "600"} color={pi === 4 ? TOTAL_ACCENT : "gray.700"} textAlign="right">
                        {fmtS3QuarterCell(v)}
                      </Td>
                    ))}
                  </Tr>
                  {isOpen && (
                    <>
                      <Tr bg="gray.50">
                        <Td py={2} pl={4} pr={1} width="32px" />
                        <Td py={2} pl={1} pr={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="700" textAlign="left">{childLabel}</Td>
                        {S3_PERIOD_HEADS.map((h) => (
                          <Td key={h} py={2} px={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="700" textAlign="right">
                            {h === "Year" ? "Total" : h}
                          </Td>
                        ))}
                      </Tr>
                      {children.map((child) => (
                        <Tr key={`${key}-${child.name || child.label}`} bg="gray.50">
                          <Td py={2.5} pl={4} pr={1} width="32px" />
                          <Td py={2.5} pl={1} pr={4} fontSize="13px" color="gray.600" textAlign="left">
                            <HStack spacing={2}>
                              <Box w="6px" h="6px" bg={TOTAL_ACCENT} opacity={0.6} borderRadius="sm" />
                              <Text>{child.label || child.name}</Text>
                            </HStack>
                          </Td>
                          {child.periods.map((v, pi) => (
                            <Td key={pi} py={2.5} px={4} fontSize="12px" fontWeight={pi === 4 ? "700" : "600"} color={pi === 4 ? TOTAL_ACCENT : "gray.600"} textAlign="right">
                              {fmtS3QuarterCell(v)}
                            </Td>
                          ))}
                        </Tr>
                      ))}
                    </>
                  )}
                </Fragment>
              );
            })
          )}
          {!loading && rows.length > 0 && (
            <Tr bg="gray.50" borderTop="2px solid" borderColor="gray.200">
              <Td py={4} px={4} colSpan={2} fontSize="13px" color="darkTeal" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em" textAlign="left">Total</Td>
              {colTotals.map((v, pi) => (
                <Td key={pi} py={4} px={4} fontSize="13px" color="darkTeal" fontWeight="700" textAlign="right">{fmtS3QuarterCell(v)}</Td>
              ))}
            </Tr>
          )}
        </Tbody>
      </Table>
    </Box>
  );
};

// "By dimension" — same Scope-1 table styling. One row per next-level item
// with its total Scope 3, YoY, and share-of-Scope-3 bar. Expanding shows
// the item's per-category split (the real per-dimension breakdown, matrix
// transposed). All values are real backend data (ESG-3942).
const DimensionTable = ({ itemRows, itemTotal, matrix, levelName, unattributed = null, shownCount = 0, totalCount = null, levelOptions = [], selectedLevelKey = null, autoLevelKey = null, onSelectLevel = null, loading, quarterly = false, setQuarterly = null, quarterlyRows = [], quarterlyLoading = false, year = null }) => {
  const [expanded, setExpanded] = useState({});
  const toggle = (k) => setExpanded((prev) => ({ ...prev, [k]: !prev[k] }));
  // ESG-3960: keyed by dimension item ID, not name.
  const rowIds = (itemRows || []).map((r) => r.id);
  const openCount = rowIds.filter((id) => expanded[id]).length;
  const expandAll = () => setExpanded(Object.fromEntries(rowIds.map((id) => [id, true])));
  const collapseAll = () => setExpanded({});
  const levelSelectable = onSelectLevel && levelOptions.length > 0;
  const levelLabel = levelName ? levelName.toLowerCase() : "dimension";
  const truncated = totalCount != null && shownCount > 0 && totalCount > shownCount;

  if (loading && !quarterly) {
    return (
      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={8}>
        <Text textAlign="center" color="gray.400" fontSize="13px">Loading…</Text>
      </Box>
    );
  }
  if ((!itemRows || itemRows.length === 0) && !quarterly) {
    return (
      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={8}>
        {/* ESG-3960: distinguish "no data" from "not tagged at this level" — the second understates a
            footprint if read as the first, and on staging it is 80% of Scope 3. */}
        <Text textAlign="center" color="gray.500" fontSize="13px">
          {unattributed != null
            ? `No Scope 3 is assigned to a ${levelLabel}. ${formatTco2(unattributed)} tCO₂e is recorded for this year but carries no ${levelLabel}.`
            : `No Scope 3 emissions recorded against this ${levelLabel} for this year.`}
        </Text>
      </Box>
    );
  }

  // Helper: pull every category's contribution for a given item. Matched by id (ESG-3960) — matched by
  // name, two same-name items resolved to the same category split.
  const categoriesForItem = (itemId) =>
    matrix
      .map((cat) => {
        const r = cat.byItem.find((x) => String(x.id) === String(itemId));
        return r ? { cat, value: r.value } : null;
      })
      .filter(Boolean)
      .sort((a, b) => b.value - a.value);

  return (
    <Box>
      <Flex justify="space-between" align="flex-end" mb={3}>
        {levelSelectable ? (
          <Menu>
            <MenuButton
              as={Button}
              variant="ghost"
              px={1}
              h="auto"
              fontSize="22px"
              fontWeight="700"
              color="darkTeal"
              rightIcon={<CaretDown size={16} weight="bold" />}
              _hover={{ bg: "gray.50" }}
              _active={{ bg: "gray.100" }}
            >
              By {levelLabel}
            </MenuButton>
            <MenuList borderRadius="lg" border="1px solid" borderColor="gray.200" boxShadow="md" py={1}>
              <MenuItem
                onClick={() => onSelectLevel(null)}
                fontWeight={selectedLevelKey === autoLevelKey ? "bold" : "normal"}
              >
                <Flex align="center" gap={2} w="full" justify="space-between">
                  <Text>Auto</Text>
                  <Text fontSize="11px" color="gray.400">level below filter</Text>
                </Flex>
              </MenuItem>
              {levelOptions.map((opt) => (
                <MenuItem
                  key={opt.key}
                  onClick={() => onSelectLevel(opt.key)}
                  fontWeight={selectedLevelKey === opt.key ? "bold" : "normal"}
                >
                  {opt.name}
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        ) : (
          <Text fontSize="22px" fontWeight="700" color="darkTeal">By {levelLabel}</Text>
        )}
        <HStack spacing={4}>
          {setQuarterly && <QuartersSwitch isChecked={quarterly} onChange={setQuarterly} />}
          <Text fontSize="13px" color="gray.500">
            {quarterly
              ? `${levelName || "Dimension"} emissions by quarter (tCO₂e); expand for the category split.`
              : `Click a row to see the category split for that ${levelName?.toLowerCase() || "item"}.`}
          </Text>
        </HStack>
      </Flex>
      {quarterly ? (
        <ScopeThreeQuarterlyTable
          firstColLabel={levelName ? levelName.toUpperCase() : "DIMENSION"}
          childLabel="Category"
          rows={quarterlyRows}
          loading={quarterlyLoading}
          year={year}
        />
      ) : (
      <Box border="1px solid" borderColor="gray.200" borderRadius="md" overflow="hidden" bg="white">
        <Table size="sm" variant="unstyled">
          <Thead>
            <Tr bg="gray.50">
              <Th py={3} px={4} colSpan={2} textAlign="left" fontSize="11px" color={TOTAL_ACCENT} textTransform="uppercase" letterSpacing="0.04em" fontWeight="700">
                <HStack spacing={2}>
                  <Box w="8px" h="8px" bg={TOTAL_ACCENT} borderRadius="sm" />
                  <Text>{levelName ? `${levelName.toUpperCase()}` : "DIMENSION"}</Text>
                  {rowIds.length > 0 && (
                    <ExpandCollapseControl
                      allExpanded={openCount === rowIds.length}
                      onExpandAll={expandAll}
                      onCollapseAll={collapseAll}
                    />
                  )}
                </HStack>
              </Th>
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">Emissions (tCO₂e)</Th>
              <Th py={3} px={4} fontSize="11px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">Share of Scope 3</Th>
            </Tr>
          </Thead>
          <Tbody>
            {itemRows.map((row, idx) => {
              const isOpen = !!expanded[row.id];
              const share = itemTotal > 0 ? row.value / itemTotal : 0;
              const cats = categoriesForItem(row.id);
              const rowClickable = cats.length > 0;
              return (
                <Fragment key={row.id}>
                  <Tr
                    borderTop={idx === 0 ? "none" : "1px solid"}
                    borderColor="gray.100"
                    cursor={rowClickable ? "pointer" : "default"}
                    _hover={rowClickable ? { bg: "gray.50" } : undefined}
                    onClick={rowClickable ? () => toggle(row.id) : undefined}
                  >
                    <Td py={4} pl={4} pr={1} width="32px">
                      {rowClickable ? (
                        <IconButton
                          size="xs"
                          variant="ghost"
                          aria-label={isOpen ? "Collapse" : "Expand"}
                          icon={isOpen ? <CaretDown size={12} weight="bold" /> : <CaretRight size={12} weight="bold" />}
                          onClick={(e) => { e.stopPropagation(); toggle(row.id); }}
                        />
                      ) : (
                        <Box w="20px" />
                      )}
                    </Td>
                    <Td py={4} pl={1} pr={4} fontSize="14px" color="gray.700" fontWeight="500" textAlign="left">
                      {row.name}
                    </Td>
                    <Td py={4} px={4} fontSize="14px" color={TOTAL_ACCENT} fontWeight="600" textAlign="right">
                      {formatTco2(row.value)}{" "}
                      {row.pctChange != null && (
                        <Text as="span" fontSize="11px" color={row.pctChange < 0 ? "green.600" : "red.600"} fontWeight="600" ml={2}>
                          {row.pctChange < 0 ? "↓" : "↑"} {Math.abs(row.pctChange).toFixed(1)}%
                        </Text>
                      )}
                    </Td>
                    <Td py={4} px={4} textAlign="left" minW="220px">
                      <HStack spacing={3}>
                        <Box flex="1" h="6px" bg="gray.100" borderRadius="full" overflow="hidden">
                          <Box h="100%" w={`${share * 100}%`} bg={TOTAL_ACCENT} borderRadius="full" />
                        </Box>
                        <Text fontSize="13px" color="gray.700" fontWeight="600" minW="48px" textAlign="right">
                          {(share * 100).toFixed(1)}%
                        </Text>
                      </HStack>
                    </Td>
                  </Tr>

                  {isOpen && rowClickable && (
                    <>
                      <Tr bg="gray.50">
                        <Td py={2} pl={4} pr={1} width="32px" />
                        <Td py={2} pl={1} pr={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">Category</Td>
                        <Td py={2} px={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="right">Emissions (tCO₂e)</Td>
                        <Td py={2} px={4} fontSize="10px" color="gray.500" textTransform="uppercase" letterSpacing="0.04em" fontWeight="600" textAlign="left">{`Share of ${row.name}`}</Td>
                      </Tr>
                      {cats.map(({ cat, value }) => {
                        const accent = cat.color || TOTAL_ACCENT;
                        const itemShare = row.value > 0 ? value / row.value : 0;
                        return (
                          <Tr key={`${row.name}-${cat.index}`} bg="gray.50">
                            <Td py={2} pl={4} pr={1} width="32px" />
                            <Td py={2} pl={1} pr={4} fontSize="13px" color="gray.700">
                              <HStack spacing={2}>
                                <Box w="8px" h="8px" bg={accent} borderRadius="sm" />
                                <Text>{cat.index}. {cat.label}</Text>
                              </HStack>
                            </Td>
                            <Td py={2} px={4} fontSize="13px" color="gray.700" fontWeight="600" textAlign="right">
                              {formatTco2(value)}
                            </Td>
                            <Td py={2} px={4} textAlign="left" minW="220px">
                              <HStack spacing={3}>
                                <Box flex="1" h="5px" bg="gray.100" borderRadius="full" overflow="hidden">
                                  <Box h="100%" w={`${itemShare * 100}%`} bg={accent} borderRadius="full" />
                                </Box>
                                <Text fontSize="12px" color="gray.700" fontWeight="600" minW="44px" textAlign="right">
                                  {(itemShare * 100).toFixed(1)}%
                                </Text>
                              </HStack>
                            </Td>
                          </Tr>
                        );
                      })}
                    </>
                  )}
                </Fragment>
              );
            })}
            {itemRows.length > 0 && (
              <Tr bg="gray.50" borderTop="2px solid" borderColor="gray.200">
                <Td py={4} pl={4} pr={1} width="32px" />
                <Td py={4} pl={1} pr={4} fontSize="13px" color="darkTeal" fontWeight="700" textTransform="uppercase" letterSpacing="0.04em" textAlign="left">Total</Td>
                <Td py={4} px={4} fontSize="14px" color="darkTeal" fontWeight="700" textAlign="right">{formatTco2(itemTotal)}</Td>
                <Td py={4} px={4} textAlign="left" minW="220px">
                  <HStack spacing={3}>
                    <Box flex="1" h="6px" bg="gray.100" borderRadius="full" overflow="hidden">
                      <Box h="100%" w="100%" bg="darkTeal" borderRadius="full" />
                    </Box>
                    <Text fontSize="13px" color="darkTeal" fontWeight="700" minW="48px" textAlign="right">100.0%</Text>
                  </HStack>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
      )}
      <Text fontSize="11px" color="gray.500" mt={2}>
        {quarterly
          ? `Emissions per ${levelName?.toLowerCase() || "dimension"} item split by quarter. The year total is the whole-year figure and may exceed the sum of the four quarters when data was entered at the yearly level.`
          : `Emissions and per-category splits are per ${levelName?.toLowerCase() || "dimension"} item, calculated from finalised questionnaire data for the selected year and filter.`}
      </Text>
    </Box>
  );
};

// Wrapper that owns the Trend / Flow toggle. Trend mode also has a
// secondary Total / All-categories toggle so users can spot per-category
// anomalies without leaving the page. Flow = sankey of how the current
// year splits across Upstream/Downstream and into each category.
const Scope3Visualisation = ({ trend, categoryTrends, accent, upstream, downstream, categoryRows, trendQuarters, setTrendQuarters, year, t }) => {
  const [view, setView] = useState("trend");
  const [trendSeries, setTrendSeries] = useState("total");
  const isTrend = view === "trend";
  const isTable = view === "table";
  const showAllCategories = trendSeries === "all";
  return (
    <Box>
      <Flex justify="space-between" align="flex-end" mb={2} flexWrap="wrap" gap={3}>
        <Box>
          <Text fontSize="22px" fontWeight="700" color="darkTeal">
            {isTrend ? "5-year trend" : isTable ? "Emissions by quarter" : "Emissions flow"}
          </Text>
          <Text fontSize="13px" color="gray.500">
            {isTrend
              ? showAllCategories
                ? `Each Scope 3 category over the last five years${trendQuarters ? ", split by quarter" : ""} — flat-lines and spikes flag anomalies.`
                : `Total Scope 3 emissions across the last five reporting years${trendQuarters ? ", split by quarter" : ""}.`
              : isTable
                ? `Scope 3 figures for ${year}, split by quarter with a full-year total. Organisation-wide (no dimension filter).`
                : "How Scope 3 splits across Upstream / Downstream and into each category."}
          </Text>
        </Box>
        <HStack spacing={3}>
          {isTrend && (
            <QuartersSwitch isChecked={trendQuarters} onChange={setTrendQuarters} />
          )}
          {isTrend && (categoryTrends?.length || 0) > 0 && (
            <TrendSeriesToggle series={trendSeries} setSeries={setTrendSeries} />
          )}
          <ChartViewToggle view={view} setView={setView} />
        </HStack>
      </Flex>
      {isTrend &&
        (showAllCategories ? (
          <CategoryTrendChart series={categoryTrends} showQuarters={trendQuarters} />
        ) : (
          <TrendChart trend={trend} accent={accent} showQuarters={trendQuarters} />
        ))}
      {isTable && <Scope3QuarterlyTable year={year} t={t} />}
      {!isTrend && !isTable && (
        <Scope3SankeyChart upstream={upstream} downstream={downstream} categoryRows={categoryRows} accent={accent} />
      )}
    </Box>
  );
};

// Sub-toggle inside Trend mode: Total = single Scope 3 total line.
// All categories = one line per populated category for anomaly spotting.
const TrendSeriesToggle = ({ series, setSeries }) => {
  const options = [
    { label: "Total", value: "total" },
    { label: "All categories", value: "all" },
  ];
  return (
    <Flex bg="gray.100" borderRadius="full" p="3px">
      {options.map((option) => {
        const active = series === option.value;
        return (
          <Button
            key={option.value}
            size="xs"
            borderRadius="full"
            bg={active ? "pureBlack" : "transparent"}
            color={active ? "white" : "gray.500"}
            fontWeight={active ? "600" : "normal"}
            onClick={() => setSeries(option.value)}
            _hover={{ bg: active ? "pureBlack" : "gray.200" }}
            px={3}
          >
            {option.label}
          </Button>
        );
      })}
    </Flex>
  );
};

// Quarter-by-quarter table of the page's KPI-card figures (Total / Upstream /
// Downstream) plus a row per populated Scope 3 category. Mounted only while
// the Table view is selected. Data comes from the same /reports/total-emissions
// per-quarter payloads the footprint table uses (org-wide — the endpoint has
// no dimension filter).
const Scope3QuarterlyTable = ({ year, t }) => {
  const compare = useQuarterlyTableCompare();
  const currentQueries = useGetQuarterlyCompanyTotalEmissions(year, {
    retry: false,
    cacheTime: 60_000,
  });
  const prevQueries = useGetQuarterlyCompanyTotalEmissions(year - 1, {
    retry: false,
    cacheTime: 60_000,
    enabled: compare.needsPreviousYear,
  });
  const payloads = currentQueries.map((q) => (q.status === "success" ? q.data : null));
  const prevPayloads = prevQueries.map((q) => (q.status === "success" ? q.data : null));

  const sumCats = (d, indexes) =>
    scopeThreeCategories
      .filter((cat) => indexes.includes(cat.index))
      .reduce((acc, cat) => acc + (Number(d?.[`category${cat.numberKey}TotalCo2Emissions`]) || 0), 0) || null;

  const headRows = [
    { label: "Total", bold: true, get: (d) => d?.scopeThreeTotalCo2Emissions },
    { label: "Upstream (Cat 1–8)", get: (d) => sumCats(d, UPSTREAM_INDEXES) },
    { label: "Downstream (Cat 9–15)", get: (d) => sumCats(d, DOWNSTREAM_INDEXES) },
  ];
  const catRows = scopeThreeCategories.map((cat) => ({
    label: `${cat.index}. ${t(cat.translationKey)}`,
    indent: true,
    get: (d) => Number(d?.[`category${cat.numberKey}TotalCo2Emissions`]) || null,
  }));

  const rows = [...headRows, ...catRows].map((row) => ({
    label: row.label,
    bold: row.bold,
    indent: row.indent,
    values: payloads.map((d) => row.get(d)),
    prevValues: prevPayloads.map((d) => row.get(d)),
  }));

  const isLoading =
    currentQueries.some((q) => q.isLoading) ||
    (compare.needsPreviousYear && prevQueries.some((q) => q.isLoading));

  return <QuarterlyBreakdownTable year={year} rows={rows} isLoading={isLoading} compare={compare} />;
};

// Pill toggle styled to match the Summary/Visual switch elsewhere on the
// detail pages.
const ChartViewToggle = ({ view, setView }) => {
  const options = [
    { label: "Trend", value: "trend", icon: <ChartLine size={14} /> },
    { label: "Flow", value: "sankey", icon: <FlowArrow size={14} /> },
    { label: "Table", value: "table", icon: <ListBullets size={14} /> },
  ];
  return (
    <Flex bg="gray.100" borderRadius="full" p="3px">
      {options.map((option) => {
        const active = view === option.value;
        return (
          <Button
            key={option.value}
            size="xs"
            borderRadius="full"
            bg={active ? "pureBlack" : "transparent"}
            color={active ? "white" : "gray.500"}
            fontWeight={active ? "600" : "normal"}
            rightIcon={option.icon}
            onClick={() => setView(option.value)}
            _hover={{ bg: active ? "pureBlack" : "gray.200" }}
            px={3}
          >
            {option.label}
          </Button>
        );
      })}
    </Flex>
  );
};

// Three-tier sankey: Scope 3 total → Upstream/Downstream → each populated
// category. Filters out categories with zero emissions (sankey rejects
// zero-weight links). Upstream stream uses the upstream KPI accent;
// downstream uses the downstream accent; the trunk uses the Scope 3 total
// accent — so the colour story matches the KPI cards above.
const Scope3SankeyChart = ({ upstream, downstream, categoryRows, accent }) => {
  const { nodes, links, labels } = useMemo(() => {
    // All populated categories, sorted by emissions desc — regardless of
    // whether they're upstream or downstream. With sort="input" on the
    // sankey, this puts the largest category at the top of the right-hand
    // column and the smallest at the bottom, even if that means upstream
    // and downstream branches cross in the middle column. Crossings are
    // an accepted trade-off for a clean size ordering on the leaves.
    const allCategories = (categoryRows || [])
      .filter((c) => c.current > 0)
      .sort((a, b) => b.current - a.current);

    const builtNodes = [];
    const builtLinks = [];

    if ((upstream || 0) <= 0 && (downstream || 0) <= 0) {
      return { nodes: [], links: [], labels: new Map() };
    }

    builtNodes.push({ id: "Scope 3", nodeColor: accent });
    if ((upstream || 0) > 0) {
      builtNodes.push({ id: "Upstream", nodeColor: UPSTREAM_ACCENT });
      builtLinks.push({ source: "Scope 3", target: "Upstream", value: Number(upstream.toFixed(2)) });
    }
    if ((downstream || 0) > 0) {
      builtNodes.push({ id: "Downstream", nodeColor: DOWNSTREAM_ACCENT });
      builtLinks.push({ source: "Scope 3", target: "Downstream", value: Number(downstream.toFixed(2)) });
    }

    allCategories.forEach((c) => {
      const id = `${c.index}. ${c.label}`;
      const isUpstream = UPSTREAM_INDEXES.includes(c.index);
      const sourceId = isUpstream ? "Upstream" : "Downstream";
      const fallbackColor = isUpstream ? UPSTREAM_ACCENT : DOWNSTREAM_ACCENT;
      builtNodes.push({ id, nodeColor: c.color || fallbackColor });
      builtLinks.push({ source: sourceId, target: id, value: Number(c.current.toFixed(2)) });
    });

    return toSafariSafeSankey(builtNodes, builtLinks);
  }, [upstream, downstream, categoryRows, accent]);

  if (nodes.length === 0) {
    return (
      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={8}>
        <Text textAlign="center" color="gray.500" fontSize="13px">
          No Scope 3 emissions data has been entered for this year.
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4} h={{ base: "420px", md: "480px" }}>
        <ResponsiveSankey
          data={{ nodes, links }}
          margin={{ top: 12, right: 200, bottom: 12, left: 100 }}
          align="justify"
          sort="input"
          colors={(node) => node.nodeColor}
          nodeOpacity={1}
          nodeHoverOthersOpacity={0.35}
          nodeThickness={14}
          nodeSpacing={18}
          nodeBorderWidth={0}
          nodeBorderRadius={2}
          linkOpacity={0.45}
          linkHoverOthersOpacity={0.1}
          linkContract={2}
          enableLinkGradient
          labelPosition="outside"
          labelOrientation="horizontal"
          labelPadding={10}
          labelTextColor={{ from: "color", modifiers: [["darker", 1.6]] }}
          label={(node) => labels.get(node.id) || node.id}
          theme={chartTheme}
          nodeTooltip={({ node }) => (
            <Box bg="white" px={3} py={2} borderRadius="md" boxShadow="md" border="1px solid" borderColor="gray.200" fontSize="12px">
              <Text fontWeight="700" color="gray.800">{labels.get(node.id) || node.id}</Text>
              <Text color="gray.600">
                {Number(node.value).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} tCO₂e
              </Text>
            </Box>
          )}
          linkTooltip={({ link }) => (
            <Box bg="white" px={3} py={2} borderRadius="md" boxShadow="md" border="1px solid" borderColor="gray.200" fontSize="12px">
              <Text fontWeight="700" color="gray.800">
                {(labels.get(link.source.id) || link.source.id)} → {(labels.get(link.target.id) || link.target.id)}
              </Text>
              <Text color="gray.600">
                {Number(link.value).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} tCO₂e
              </Text>
            </Box>
          )}
        />
      </Box>
      <Text fontSize="11px" color="gray.500" mt={2}>
        Categories with no recorded emissions for this year are hidden so the layout reads cleanly.
      </Text>
    </Box>
  );
};

// Convert a 6-digit hex (#RRGGBB) into an `rgba()` string with the given
// alpha. Used to derive a light-tint chip background from each category's
// solid hex without needing a per-category `lightColor` constant.
const hexToRgba = (hex, alpha) => {
  if (typeof hex !== "string") return `rgba(0, 0, 0, ${alpha})`;
  const m = hex.replace("#", "");
  const r = parseInt(m.slice(0, 2), 16) || 0;
  const g = parseInt(m.slice(2, 4), 16) || 0;
  const b = parseInt(m.slice(4, 6), 16) || 0;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Multi-series 5-year chart — one line per Scope 3 category. Each
// category renders as a clickable pill above the chart so users can hide
// dominant categories to see what's happening underneath. Active pill =
// tinted background + close (×) icon; inactive = empty outline. Same UX
// pattern the main Emissions Trends accordion uses for scope toggling.
const CategoryTrendChart = ({ series, showQuarters = false }) => {
  const [hidden, setHidden] = useState(() => new Set());
  const toggle = (id) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  if (!series || series.length === 0) {
    return (
      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={8}>
        <Text textAlign="center" color="gray.500" fontSize="13px">
          {showQuarters
            ? "No quarterly Scope 3 data — emissions for these years were reported at the yearly level only."
            : "No Scope 3 emissions data has been entered yet."}
        </Text>
      </Box>
    );
  }
  const colorById = {};
  series.forEach((s) => { colorById[s.id] = s.color; });
  const visible = series.filter((s) => !hidden.has(s.id));

  return (
    <Box>
      <HStack spacing={2} mb={3} flexWrap="wrap" rowGap={2}>
        {series.map((s) => {
          const active = !hidden.has(s.id);
          const baseColor = s.color || "#5B1D99";
          return (
            <Tag
              key={s.id}
              size="md"
              borderRadius="full"
              cursor="pointer"
              border="1px solid"
              borderColor={active ? baseColor : "gray.300"}
              bg="transparent"
              color={active ? baseColor : "gray.500"}
              fontWeight="600"
              sx={active ? {
                // Keep the close icon coloured to match the label/border
                // when the pill is active. Chakra's default close-button
                // styling washes it out otherwise.
                ".chakra-tag__close-btn": { color: baseColor, opacity: 1 },
                ".chakra-tag__close-btn:hover": { color: baseColor, bg: hexToRgba(baseColor, 0.12) },
              } : undefined}
              onClick={() => !active && toggle(s.id)}
              _hover={{ bg: active ? hexToRgba(baseColor, 0.08) : "gray.50" }}
              transition="all 0.15s"
            >
              <TagLabel>{s.id}</TagLabel>
              {active && (
                <TagCloseButton onClick={(e) => { e.stopPropagation(); toggle(s.id); }} />
              )}
            </Tag>
          );
        })}
      </HStack>
      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4} h={{ base: "420px", md: "440px" }}>
        {visible.length === 0 ? (
          <Flex align="center" justify="center" h="100%">
            <Text color="gray.400" fontSize="13px">All categories hidden — click a pill to show one.</Text>
          </Flex>
        ) : (
          <ResponsiveLine
            data={visible.map(({ id, data }) => ({ id, data }))}
            layers={["grid", "markers", "axes", axisBaselineLayer, "areas", "crosshair", "lines", "points", "slices", "mesh", "legends"]}
            margin={{ top: 12, right: 24, bottom: showQuarters ? 56 : 36, left: 64 }}
            xScale={{ type: "point" }}
            // Stack the categories so they build on top of each other
            // and the top edge of the topmost area = the cumulative
            // Scope 3 total for that year. Areas are filled with the
            // category accent at low opacity to keep the boundary line
            // readable against the band below.
            yScale={{ type: "linear", min: 0, max: "auto", stacked: true }}
            colors={({ id }) => colorById[id] || "#5B1D99"}
            curve="monotoneX"
            enableArea
            areaOpacity={0.55}
            areaBlendMode="normal"
            enablePoints
            pointSize={5}
            pointBorderWidth={2}
            pointBorderColor={{ from: "serieColor" }}
            pointColor="#FFFFFF"
            axisBottom={{ tickSize: 0, tickPadding: 6, tickRotation: showQuarters ? -45 : 0 }}
            axisLeft={{
              tickSize: 0,
              tickPadding: 6,
              format: (v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v),
            }}
            enableGridX={false}
            theme={chartTheme}
            // Tooltip shows the category's own value (point.data.y) plus
            // the cumulative stack total at that x (point.data.yStacked)
            // so users can read both the per-category contribution and
            // the running total in one place.
            tooltip={({ point }) => (
              <Box bg="white" px={3} py={2} borderRadius="md" boxShadow="md" border="1px solid" borderColor="gray.200" fontSize="12px">
                <Text fontWeight="700" color="gray.800">{point.serieId}</Text>
                <Text color={point.serieColor}>
                  {point.data.x} · {Number(point.data.y).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} tCO₂e
                </Text>
                {point.data.yStacked != null && (
                  <Text color="gray.500" fontSize="11px">
                    Cumulative: {Number(point.data.yStacked).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} tCO₂e
                  </Text>
                )}
              </Box>
            )}
            useMesh
          />
        )}
      </Box>
      <Text fontSize="11px" color="gray.500" mt={2}>
        Click a pill to show or hide a category. Categories with no recorded emissions across the five years are omitted.
      </Text>
    </Box>
  );
};

// 5-year line chart of total Scope 3 emissions. Mirrors the per-category
// TrendChart in ScopeThreeCategoryDetail for visual consistency. Years
// without recorded data are filled in by the parent's `trend` builder so
// the line stays continuous. Title/description live on the wrapping
// Scope3Visualisation so this just renders the chart panel.
const TrendChart = ({ trend, accent, showQuarters = false }) => {
  if (!trend || trend.length === 0) {
    if (showQuarters) {
      return (
        <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={8}>
          <Text textAlign="center" color="gray.500" fontSize="13px">
            No quarterly Scope 3 data — emissions for these years were reported at the yearly level only.
          </Text>
        </Box>
      );
    }
    return null;
  }
  return (
    <Box>
      <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="md" p={4} h="320px">
        <ResponsiveLine
          data={[{ id: "Scope 3", data: trend }]}
          layers={["grid", "markers", "axes", axisBaselineLayer, "areas", "crosshair", "lines", "points", "slices", "mesh", "legends"]}
          margin={{ top: 12, right: 24, bottom: showQuarters ? 56 : 36, left: 64 }}
          xScale={{ type: "point" }}
          yScale={{ type: "linear", min: 0, max: "auto", stacked: false }}
          colors={[accent]}
          curve="monotoneX"
          enableArea
          areaOpacity={0.12}
          enablePoints
          pointSize={6}
          pointBorderWidth={2}
          pointBorderColor={accent}
          pointColor="#FFFFFF"
          axisBottom={{ tickSize: 0, tickPadding: 6, tickRotation: showQuarters ? -45 : 0 }}
          axisLeft={{
            tickSize: 0,
            tickPadding: 6,
            format: (v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v),
          }}
          enableGridX={false}
          theme={chartTheme}
          tooltip={({ point }) => (
            <Box bg="white" px={3} py={2} borderRadius="md" boxShadow="md" border="1px solid" borderColor="gray.200" fontSize="12px">
              <Text fontWeight="700" color="gray.800">{point.data.x}</Text>
              <Text color={accent}>{Number(point.data.y).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} tCO₂e</Text>
            </Box>
          )}
          useMesh
        />
      </Box>
      <Text fontSize="11px" color="gray.500" mt={2}>
        {showQuarters
          ? "Only quarters with recorded Scope 3 data are plotted — years reported at the yearly level only will not appear."
          : "Only years with recorded Scope 3 data are plotted; years without data are omitted."}
      </Text>
    </Box>
  );
};

export default ScopeThreeDetail;
